import React, {useEffect, useState} from 'react';
import {useUser} from "../Providers/UserInfo";
import {Button, Spinner} from "@heroui/react";
import {getErrorMessage, toastError} from "../lib/toast";
import {useAppNavigate} from "../lib/use-app-navigate";
import {useMutation} from "@tanstack/react-query";
import {request} from "../lib/request";
import {ReactComponent as Eye} from "../assets/icons/eye.svg";
import {ReactComponent as EyeSlash} from "../assets/icons/eye-slash.svg";
import {ReactComponent as OrdersIcon} from "../assets/icons/orders.svg";
import {ReactComponent as StoreIcon} from "../assets/icons/store.svg";
import {ReactComponent as ShoppingIcon} from "../assets/icons/shopping-bag.svg";

const MAIN_SITE_URL = 'https://ghazaresan.ir';

const FEATURES = [
    {icon: OrdersIcon, label: 'مدیریت لحظه‌ای سفارش‌ها'},
    {icon: StoreIcon, label: 'ویرایش منو و موجودی'},
    {icon: ShoppingIcon, label: 'گزارش فروش و عملکرد'},
];

function BrandMark({variant = 'dark'}) {
    return (
        <div className={`rest-login__brand rest-login__brand--${variant}`}>
            <img
                src="/images/small-logo.png"
                alt=""
                className="rest-login__brand-icon"
                width={40}
                height={40}
                decoding="async"
            />
            <span className="rest-login__brand-name">غذارسان</span>
        </div>
    );
}

const Login = () => {
    const navigate = useAppNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const {loading, sessionActive, activateSession} = useUser();

    const {mutate, isPending} = useMutation({
        mutationFn: async (data) =>
            await request({
                url: `/auth/restaurant/login`,
                method: "post",
                data,
            }),
        onSuccess: (data) => {
            activateSession(data.data.token, data.data.restaurant);
            navigate('/orders', {
                replace: true,
                state: {loginSuccess: data.message},
            });
        },
        onError: (err) => toastError(
            getErrorMessage(err, 'خطا در ورود. اتصال اینترنت را بررسی کنید.')
        ),
    });

    useEffect(() => {
        if (sessionActive && !loading) {
            navigate('/orders', {replace: true});
        }
    }, [sessionActive, loading, navigate]);

    const hand = (e) => {
        e.preventDefault();
        const form = new FormData(e.target);
        const data = Object.fromEntries(form.entries());
        mutate(data);
    };

    if (sessionActive && loading) {
        return (
            <div className="rest-login rest-login--loading">
                <div className="rest-login__loading">
                    <Spinner color="warning" size="lg"/>
                    <p>در حال انتقال به پنل...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="rest-login">
            <div className="rest-login__layout">
                <aside className="rest-login__visual" aria-hidden="true">
                    <img
                        className="rest-login__visual-bg"
                        src="/images/SellerHero.jpg"
                        alt=""
                    />
                    <div className="rest-login__visual-overlay"/>
                    <div className="rest-login__visual-content">
                        <BrandMark variant="light"/>
                        <span className="rest-login__badge">پنل رستوران‌دار</span>
                        <h1 className="rest-login__visual-title">
                            مدیریت آشپزخانه و سفارش‌ها، ساده‌تر از همیشه
                        </h1>
                        <p className="rest-login__visual-text">
                            سفارش‌های آنلاین، منوی غذا، تخفیف‌ها و گزارش‌های فروش را
                            در یک پنل یکپارچه مدیریت کنید.
                        </p>
                        <ul className="rest-login__features">
                            {FEATURES.map(({icon: Icon, label}) => (
                                <li key={label} className="rest-login__feature">
                                    <span className="rest-login__feature-icon">
                                        <Icon/>
                                    </span>
                                    <span>{label}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <img
                        className="rest-login__deco rest-login__deco--carrot"
                        src="/images/icon-carrot.png"
                        alt=""
                    />
                    <img
                        className="rest-login__deco rest-login__deco--pumpkin"
                        src="/images/icon-pumpkin2.png"
                        alt=""
                    />
                </aside>

                <main className="rest-login__main">
                    <div className="rest-login__form-wrap">
                        <div className="rest-login__form-card">
                            <div className="rest-login__form-accent" aria-hidden="true"/>

                            <div className="rest-login__form-body">
                                <header className="rest-login__form-header">
                                    <BrandMark variant="dark"/>
                                    <div className="rest-login__form-heading">
                                        <h2>ورود به پنل رستوران</h2>
                                        <p>شماره موبایل و رمز عبور ثبت‌شده را وارد کنید.</p>
                                    </div>
                                </header>

                                <form onSubmit={hand} className="rest-login__form" noValidate>
                                    <div className="rest-login__field">
                                        <label className="rest-login__label" htmlFor="mobile">
                                            شماره همراه
                                        </label>
                                        <input
                                            id="mobile"
                                            className="rest-login__input"
                                            name="mobile"
                                            type="tel"
                                            inputMode="tel"
                                            autoComplete="tel"
                                            autoFocus
                                            required
                                            dir="ltr"
                                            placeholder="09123456789"
                                        />
                                    </div>

                                    <div className="rest-login__field">
                                        <label className="rest-login__label" htmlFor="password">
                                            رمز عبور
                                        </label>
                                        <div className="rest-login__input-wrap">
                                            <input
                                                id="password"
                                                className="rest-login__input rest-login__input--password"
                                                name="password"
                                                type={showPassword ? 'text' : 'password'}
                                                autoComplete="current-password"
                                                required
                                                placeholder="رمز عبور خود را وارد کنید"
                                            />
                                            <button
                                                type="button"
                                                className="rest-login__eye"
                                                onClick={() => setShowPassword((v) => !v)}
                                                aria-label={showPassword ? 'مخفی کردن رمز' : 'نمایش رمز'}
                                            >
                                                {showPassword ? <Eye/> : <EyeSlash/>}
                                            </button>
                                        </div>
                                    </div>

                                    <Button
                                        type="submit"
                                        isLoading={isPending}
                                        className="rest-login__submit"
                                        radius="lg"
                                    >
                                        ورود به پنل
                                    </Button>
                                </form>

                                <p className="rest-login__terms">
                                    ورود به پنل غذارسان به منزله پذیرش{' '}
                                    <a
                                        href={`${MAIN_SITE_URL}/terms`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="rest-login__link"
                                    >
                                        قوانین و مقررات
                                    </a>{' '}
                                    سامانه است.
                                </p>
                            </div>
                        </div>

                        <p className="rest-login__support">
                            مشکل در ورود دارید؟ با{' '}
                            <a
                                href={`${MAIN_SITE_URL}/contact`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="rest-login__link"
                            >
                                پشتیبانی غذارسان
                            </a>{' '}
                            تماس بگیرید.
                        </p>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default Login;
